/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '../Modal'
import { removeDraftVersions } from '../../services/api/files'
import { useServiceContext } from '../../root'
import { useEsignature } from '../../hooks/useEsignature'

export interface RemoveDraftsModalProps {
  fsiId: number
  versionId: number
  onClose?: () => void
  onSuccess?: () => void
}

export const RemoveDraftsModal: React.FC<RemoveDraftsModalProps> = ({ fsiId, versionId, onClose, onSuccess }) => {
  const { t } = useTranslation()
  const context = useServiceContext()
  const { signAction } = useEsignature()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSave = async () => {
    try {
      setIsSubmitting(true)

      // Check if e-signature is required
      let validation: any = undefined
      if (context.service.auth.requireESignatureForFileCommands()) {
        // Use signAction helper which handles ADMIN_ACTION text pattern
        validation = await signAction('ADMIN_ACTION', 'REMOVE_DRAFT_VERSIONS')
      }

      // Call the API to remove draft versions
      await removeDraftVersions(fsiId, versionId, validation)

      // Show success message
      context.service.toast.success(t('REMOVE_DRAFT_VERSIONS_SUCCESSFULLY_REMOVED'))

      // Close modal and trigger success callback
      if (onSuccess) {
        onSuccess()
      }
      if (onClose) {
        onClose()
      }
    } catch (error: any) {
      console.error('Error removing draft versions:', error)
      // Match legacy error handling: ERROR_ERROR with error message parameter
      const errorMessage = error?.data?.message || error?.message || 'Unknown error'
      context.service.toast.error(t('ERROR_ERROR', { error: errorMessage }))
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting && onClose) {
      onClose()
    }
  }

  return (
    <Modal show={true} handleClose={handleClose} title={t('REMOVE')}>
      <Modal.Body>
        <p>{t('REMOVE_DRAFT_VERSIONS_TEXT')}</p>
        <p>
          <strong>{versionId}.0</strong>
        </p>
      </Modal.Body>

      <Modal.Footer>
        <button type="button" className="btn btn-default" onClick={handleClose} disabled={isSubmitting}>
          {t('NO')}
        </button>
        <button type="button" className="btn btn-primary" onClick={handleSave} disabled={isSubmitting}>
          {t('YES')}
        </button>
      </Modal.Footer>
    </Modal>
  )
}

export default RemoveDraftsModal
